export type WeekStart = "sunday" | "monday";
export interface CalendarPlacement { date: string; minute?: number }
export const CALENDAR_SLOT_MINUTES = 30;

export function localSlotInstant(date: string, minuteOfDay: number): string {
  const bounded = Math.max(0, Math.min(1439, Math.round(minuteOfDay / CALENDAR_SLOT_MINUTES) * CALENDAR_SLOT_MINUTES));
  const hours = String(Math.floor(bounded / 60)).padStart(2, "0");
  const minutes = String(bounded % 60).padStart(2, "0");
  return new Date(`${date}T${hours}:${minutes}:00`).toISOString();
}

export function taskDurationMinutes(startAt?: string | null, endAt?: string | null, estimate?: number | null): number {
  if (startAt && endAt) {
    const minutes = Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60_000);
    if (minutes > 0) return Math.max(CALENDAR_SLOT_MINUTES, minutes);
  }
  return Math.max(CALENDAR_SLOT_MINUTES, estimate || 60);
}

export function calendarTaskPlacement(startAt: string, endAt?: string | null, estimate?: number | null): { startMinute: number; duration: number } {
  const start = new Date(startAt);
  return { startMinute: start.getHours() * 60 + start.getMinutes(), duration: taskDurationMinutes(startAt, endAt, estimate) };
}

export function rangesOverlap(leftStart: number, leftDuration: number, rightStart: number, rightDuration: number): boolean {
  return leftStart < rightStart + rightDuration && rightStart < leftStart + leftDuration;
}

function addUtcDays(date: string, amount: number): string {
  const value = new Date(`${date}T00:00:00Z`); value.setUTCDate(value.getUTCDate() + amount); return value.toISOString().slice(0, 10);
}

export function calendarRangeDates(anchorDate: string, mode: "day" | "week" | "month", weekStart: WeekStart): string[] {
  if (mode === "day") return [anchorDate];
  const anchor = new Date(`${anchorDate}T00:00:00Z`);
  const firstDay = weekStart === "monday" ? 1 : 0;
  if (mode === "week") {
    const start = addUtcDays(anchorDate, -((anchor.getUTCDay() - firstDay + 7) % 7));
    return Array.from({ length: 7 }, (_, index) => addUtcDays(start, index));
  }
  const first = `${anchorDate.slice(0, 7)}-01`;
  const firstWeekday = new Date(`${first}T00:00:00Z`).getUTCDay();
  const start = addUtcDays(first, -((firstWeekday - firstDay + 7) % 7));
  const last = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0));
  const lastDate = last.toISOString().slice(0, 10);
  const lastWeekday = last.getUTCDay();
  const end = addUtcDays(lastDate, (firstDay + 6 - lastWeekday + 7) % 7);
  const count = Math.round((new Date(`${end}T00:00:00Z`).getTime() - new Date(`${start}T00:00:00Z`).getTime()) / 86_400_000) + 1;
  return Array.from({ length: count }, (_, index) => addUtcDays(start, index));
}

export function isoWeekNumber(date: string): number {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 4 - (value.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(value.getUTCFullYear(), 0, 1));
  return Math.ceil((((value.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7);
}
